import { createContext, useContext, useEffect, useState } from "react";

export type AuthUser = { oid: string; name: string; email: string } | null;

export const AuthContext = createContext<AuthUser>(null);
export function useAuth() { return useContext(AuthContext); }

/**
 * Fetches the current user once, stores it in context, and redirects to login if unauthenticated.
 */
export default function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser>(null);
  const [state, setState] = useState<"loading" | "authenticated">("loading");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/auth/me");
        if (cancelled) return;
        if (res.ok) {
          setUser(await res.json());
          setState("authenticated");
        } else if (res.status === 401) {
          // Server is up and explicitly says we're not authenticated → log in.
          window.location.href = "/api/auth/login";
        } else {
          // Other HTTP errors: don't redirect-loop; show the app without a user.
          // (UserMenu will simply hide itself when user is null.)
          console.warn("auth/me returned", res.status);
          setState("authenticated");
        }
      } catch (err) {
        // Network error — auth endpoint unreachable. Render the app anyway so
        // the user isn't trapped on a loading screen. This also covers the
        // "auth disabled" deployment mode.
        if (!cancelled) {
          console.warn("auth/me unreachable:", err);
          setState("authenticated");
        }
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (state === "loading") {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-950 text-gray-400">
        <span className="animate-pulse">Loading…</span>
      </div>
    );
  }

  return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
}
